import { Box, Typography } from "@mui/material";
import _ from "lodash";
import { ReactElement } from "react";

import { CampaignStatus } from "~frontend/__generated__/graphql";
import { FlexBox } from "~frontend/components/FlexBox";
import { InternalLink } from "~frontend/components/Link";
import Loading from "~frontend/components/Loading";
import Table from "~frontend/components/Table";
import TooltipButton from "~frontend/components/TooltipButton";
import { useSubaccount } from "~frontend/providers/SubaccountProvider";
import { CAMPAIGN_STATUSES } from "~frontend/routes/subaccounts/campaignsList/constants";
import { CampaignWithSuppressionData } from "~frontend/routes/types";
import { jobInProgress } from "~frontend/util/jobs";

export default function OverlapTable({
  campaign,
  filteredOverlaps,
  selectedOverlapIds,
  setSelectedOverlapIds,
  onSuppressSelected,
  suppressSubmitting,
  anySuppressionJobRunning,
  onOpenFilterDialog,
  createdAtWindowChanged,
  page,
  setPage,
  pageSize,
  setPageSize,
}: {
  campaign: CampaignWithSuppressionData;
  filteredOverlaps: CampaignWithSuppressionData["campaignOverlaps"][number][];
  selectedOverlapIds: string[];
  setSelectedOverlapIds: (ids: string[]) => void;
  onSuppressSelected: () => Promise<void>;
  suppressSubmitting: boolean;
  anySuppressionJobRunning: boolean;
  onOpenFilterDialog: () => void;
  createdAtWindowChanged: boolean;
  page: number;
  setPage: (newPage: number) => void;
  pageSize: number;
  setPageSize: (newPageSize: number) => void;
}): ReactElement {
  const { subaccountLink } = useSubaccount()!;

  if (
    campaign.calculateCampaignOverlapsJob &&
    jobInProgress(campaign.calculateCampaignOverlapsJob)
  ) {
    return (
      <Loading
        message={
          campaign.calculateCampaignOverlapsJob.progressMessage ??
          "Recalculating overlaps..."
        }
        progress={
          campaign.calculateCampaignOverlapsJob.progress
            ? campaign.calculateCampaignOverlapsJob.progress / 100
            : undefined
        }
      />
    );
  }

  if (anySuppressionJobRunning) {
    return <Loading message="Recalculating overlaps..." />;
  }

  if (createdAtWindowChanged) {
    return (
      <Box sx={{ textAlign: "center", mb: 2 }}>
        Click "Refresh Campaigns" above to see overlaps
      </Box>
    );
  }

  const rows = filteredOverlaps
    .slice(page * pageSize, (1 + page) * pageSize)
    .map(overlap => ({
      id: overlap.id,
      campaignName: overlap.overlappingCampaign.name,
      campaignId: overlap.overlappingCampaign.id,
      campaignShortId: overlap.overlappingCampaign.shortId,
      status: overlap.overlappingCampaign.status,
      createdAt: overlap.overlappingCampaign.createdAt,
      overlapCount: overlap.overlapCount,
      alreadySuppressedOverlapCount: overlap.alreadySuppressedOverlapCount,
    }));

  const unstartedCampaignSelected = filteredOverlaps.some(
    o =>
      selectedOverlapIds.includes(o.id) &&
      o.overlappingCampaign.status === CampaignStatus.Draft
  );

  const sendInProgress =
    campaign.status === CampaignStatus.InitialSendRunning ||
    campaign.status === CampaignStatus.InitialSendCanceling;

  return (
    <>
      <Table
        page={page}
        onChangePage={setPage}
        rowsPerPage={pageSize}
        onChangeRowsPerPage={setPageSize}
        totalCount={filteredOverlaps.length}
        rows={rows}
        emptyState="No matching overlaps found"
        selectedRows={selectedOverlapIds}
        onChangeSelectedRows={setSelectedOverlapIds}
        columns={[
          {
            key: "campaignName",
            title: "Campaign",
            renderValue: row => (
              <>
                <InternalLink
                  to={subaccountLink(`/campaigns/${row.campaignShortId}`)}
                  target="_blank"
                >
                  {row.campaignName}
                </InternalLink>
                <Typography variant="body3" component={"div"}>
                  Campaign ID: #{row.campaignShortId}
                </Typography>
              </>
            ),
          },
          {
            key: "status",
            title: "Status",
            renderValue: row =>
              CAMPAIGN_STATUSES.find(s => s.value === row.status)?.label,
          },
          {
            key: "createdAt",
            title: "Created",
            renderValue: row => new Date(row.createdAt).toLocaleString(),
          },
          {
            key: "overlapCount",
            title: "Overlaps",
            renderValue: row => (
              <>
                {row.overlapCount.toLocaleString()}
                {row.alreadySuppressedOverlapCount > 0 && (
                  <>
                    <br />
                    <Typography variant="caption" color="textSecondary">
                      {row.alreadySuppressedOverlapCount.toLocaleString()}{" "}
                      already suppressed
                    </Typography>
                  </>
                )}
              </>
            ),
          },
        ]}
        reverseFooterOrder
        footerActions={
          <FlexBox
            gap="10px"
            justifyContent="flex-end"
            sx={{
              "& .MuiButton-root": {
                fontSize: "10px",
                px: 1,
              },
            }}
          >
            <TooltipButton
              variant="contained"
              color="primary"
              onClick={onSuppressSelected}
              tooltipText={
                sendInProgress
                  ? "Cannot suppress contacts while sending is in progress"
                  : undefined
              }
              disabled={
                suppressSubmitting ||
                selectedOverlapIds.length === 0 ||
                sendInProgress
              }
              sx={{ fontSize: "10px", padding: "6px 8px", height: "auto" }}
            >
              Suppress Selected
            </TooltipButton>
            <TooltipButton
              variant="contained"
              color="primary"
              onClick={onOpenFilterDialog}
              tooltipText={
                sendInProgress
                  ? "Cannot suppress contacts while sending is in progress"
                  : unstartedCampaignSelected
                    ? "Filters do not apply to Draft campaigns"
                    : undefined
              }
              disabled={
                suppressSubmitting ||
                selectedOverlapIds.length === 0 ||
                unstartedCampaignSelected ||
                sendInProgress
              }
              sx={{ fontSize: "10px", padding: "6px 8px", height: "auto" }}
            >
              Filter Selected
            </TooltipButton>
          </FlexBox>
        }
      />
    </>
  );
}
